import { createMemo, type Accessor } from "solid-js"
import type { Engine } from "../../lib/core/engine/Engine.type"
import type { TilePositionXY } from "../../lib/core/tile_position_xy/TilePositionXY.type"
import { isPlainObjDeeplyEqual } from "../../lib/isPlainObjDeeplyEqual"
import type { Nullable } from "../../typescript"
import createSharedCachedFactory from "../memo/createSharedCachedFactory"
import { useSelectedPositionListSignal } from "./useSelectedPositionListSignal"

type SelectedPositionSignal = [
  Accessor<TilePositionXY | null>,
  (value: Nullable<TilePositionXY>) => void
]

/**
 * The root data store for selection is multi-select.
 * However, this signal allows for call sites to use
 * a zero-or-one tile positions are selected interface.
 * 
 * If multiple tile positions are selected, this signal short circuits to null.
 * 
 * example usage:
 * export const [getSelectedPosition, setSelectedPosition] = useSelectedPositionSignal(engine)
 */
export const useSelectedPositionSignal = createSharedCachedFactory((
  engine: Engine
): SelectedPositionSignal => {
  const [getSelectedPositionList, setSelectedPositionList] = useSelectedPositionListSignal(engine)
  const memo = createMemo(() => {
    const list = getSelectedPositionList()
    if (list.length === 1) {
      return list[0]
    }
    return null
  }, null, {
    equals: isPlainObjDeeplyEqual,
  })

  const setter = (input: Nullable<TilePositionXY>) => {
    const newList: Array<TilePositionXY> = []
    if (input) {
      newList.push(input)
    }
    setSelectedPositionList(newList)
  }

  const signal: SelectedPositionSignal = [memo, setter]

  return signal
})


